import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { Member } from "@shared/models/member";
import { PhonePrefixes } from "@shared/models/phone-prefixes";
import { PhonePrefixesService } from "@shared/services/phone-prefixes.service";
import { DropdownModule } from "primeng/dropdown";
import { isPartner } from "@shared/utils/brand-utils";
import { environment } from "@env/environment";

@Component({
  selector: "app-user-info",
  standalone: true,
  imports: [CommonModule, DropdownModule, ReactiveFormsModule, TranslateModule],
  templateUrl: "./user-info.component.html",
  styleUrls: ["./user-info.component.scss"],
})
export class UserInfoComponent implements OnInit, OnChanges {
  @Input() member: Member | undefined;
  @Input() isEditMode = false;
  @Output() changePasswordToggle = new EventEmitter<boolean>();
  phoneNumberPrefixes: PhonePrefixes[] = [];
  userInfoForm: FormGroup;

  constructor(private phonePrefix: PhonePrefixesService) {
    this.userInfoForm = new FormGroup({
      editedPhonePrefix: new FormControl<PhonePrefixes | string>(""),
      editedPhoneNumber: new FormControl<string>("", Validators.pattern("^[0-9]*$")),
    });
  }
  ngOnInit() {
    this.getPhoneNumberPrefix();
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes["member"]) {
      this.setPhoneNumberAndPrefix();
    }
  }
  getPhoneNumberPrefix() {
    return this.phonePrefix.getPhonePrefixes().subscribe(prefixes => {
      this.phoneNumberPrefixes = prefixes;
      this.setPhoneNumberAndPrefix();
    });
  }

  setPhoneNumberAndPrefix() {
    const prefixControl = this.userInfoForm.get(`editedPhonePrefix`);
    const numberControl = this.userInfoForm.get(`editedPhoneNumber`);
    if (this.member && prefixControl) {
      const phonePrefix = this.phoneNumberPrefixes.find(prefix => prefix.prefix === this.member?.phonePrefix);
      if (phonePrefix) {
        prefixControl.setValue(phonePrefix);
        numberControl?.setValue(this.member?.phoneNumber || "");
        const currentValidators = numberControl?.validator ? [numberControl.validator] : [];
        numberControl?.setValidators([...currentValidators, this.phoneNumberValidator()]);
        numberControl?.updateValueAndValidity();
      }
    }
  }
  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const parent = this.userInfoForm;
      if (!parent) {
        return null; // Pass validation when the control is not yet initialized
      }
      const prefixControl = parent.get("editedPhonePrefix");
      if (!prefixControl || !prefixControl.value) {
        return null; // Pass validation if prefix is not set
      }

      const additionalDigitsRequired = prefixControl.value.additionalDigitsRequired;
      const phoneNumberLength = control.value ? control.value.length : 0;
      if (phoneNumberLength === 0 || phoneNumberLength === additionalDigitsRequired) {
        return null; // Valid if no value or length matches additionalDigitsRequired
      }
      return { invalidPhoneNumber: true }; // Invalid if length does not match
    };
  }

  onPhonePrefixChange() {
    const numberControl = this.userInfoForm.get(`editedPhoneNumber`);
    if (numberControl) {
      numberControl.updateValueAndValidity();
    }
  }

  toggleChangePassword() {
    this.changePasswordToggle.emit(true);
  }

  isFormValid(): boolean {
    return this.userInfoForm.valid;
  }

  // Method to get form data
  getFormData(): any {
    return this.userInfoForm.value;
  }

  resetForm(): any {
    return this.userInfoForm.reset();
  }

  isPartner(): boolean {
    return isPartner(environment);
  }
}
